import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Mèze' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Mèze',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Mèze' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Mèze. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'L\'air de l\'étang de Thau endommage-t-il les menuiseries à Mèze ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, l\'humidité saline de l\'étang de Thau accélère l\'usure des pièces métalliques : galets, rails aluminium, mécanismes de crémone. Nous utilisons des pièces traitées anticorrosion et appliquons un entretien préventif adapté au milieu lagunaire.',
      },
    },
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans le port et le centre ancien de Mèze ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Nous nous déplaçons dans tout Mèze : port ostréicole, centre ancien, quartiers résidentiels et secteurs en bordure d\'étang. Délai 24 à 48h, 7j/7. Appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function MezePage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Mèze | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Mèze ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, pièces anticorrosion, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-meze/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Mèze"
        subtitle="L'humidité saline de l'étang de Thau attaque vos menuiseries. Nos techniciens réparent baies, volets et stores avec des pièces anticorrosion. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie abîmée par l'air salin à Mèze ?"
        ctaText="Devis gratuit, pièces anticorrosion, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Mèze, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Mèze, l'humidité saline de l'étang de Thau corrode les pièces métalliques des menuiseries. Galets de baie qui grippent, axes de volet oxydés, stores aux mécanismes rouillés : nous utilisons des pièces renforcées. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/meze-baie-01.webp"
              alt="Réparation de baie vitrée coulissante à Mèze"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : corrosion accélérée par l'étang</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les villas mézoises avec vue sur l'étang sont équipées de grandes baies coulissantes. Le sel réduit leur durée de vie : roulettes qui rouillent, rail qui se pique. Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> doit être réparée sans attendre.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes renforcées</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/serrure-cremone-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">serrure</Link> et <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : premier rempart contre le sel</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> sont en première ligne face à la corrosion. <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Volet bloqué</Link> par un axe oxydé, coulisses encrassées de dépôts salins : nous nettoyons, lubrifions et réparons.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Moteur</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> avec moteurs protégés pour zones lagunaires.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/meze-volet-02.jpg"
              alt="Réparation de volet roulant à Mèze"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/meze-vitrage-03.webp"
              alt="Remplacement de vitrage à Mèze"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Fenêtre et vitrage : protéger de l'humidité</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres</Link> subissent des cycles humidité-chaleur qui dégradent les <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>. <Link to="/fenetre-bloquee-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtre qui ferme mal</Link> ou <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link> grippée : nous intervenons.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> isolant, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Mèze sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Mèze : port ostréicole, centre ancien, route de Montagnac, abords de l'étang. Également Loupian, Villeveyrac, Bouzigues. Consultez <Link to="/depannage-menuiserie-sete/" className="text-accent-500 font-semibold hover:underline">Sète</Link> et <Link to="/depannage-menuiserie-marseillan/" className="text-accent-500 font-semibold hover:underline">Marseillan</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}